import React, { useMemo, useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Home, PanelLeftClose, X } from 'lucide-react';
import { getGroupedDocs } from './docsRegistry';
import DocsSearchBar from './DocsSearchBar';

interface SidebarProps {
  isMobileMenuOpen: boolean;
  isSidebarCollapsed: boolean;
  showHeader?: boolean;
  onToggleSidebar?: () => void;
  setIsMobileMenuOpen: (open: boolean) => void;
}

export const DocsSidebar: React.FC<SidebarProps> = ({
  isMobileMenuOpen,
  isSidebarCollapsed,
  showHeader = false,
  onToggleSidebar,
  setIsMobileMenuOpen,
}) => {
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const groupedDocs = useMemo(() => getGroupedDocs(), []);
  const allDocs = useMemo(() => Object.values(groupedDocs).flat(), [groupedDocs]);
  const currentDoc = allDocs.find(doc => `/docs/${doc.slug}` === location.pathname);
  const currentCategory = currentDoc?.category;

  useEffect(() => {
    const firstCategory = Object.keys(groupedDocs)[0];
    setOpenCategories((prev) => ({
      ...prev,
      [currentCategory || firstCategory]: true,
    }));
  }, [currentCategory, groupedDocs]);

  const toggleCategory = (category: string) => {
    setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <aside
      id="docs-sidebar"
      className={`docs-sidebar ${showHeader ? 'docs-sidebar-has-header' : ''} ${isMobileMenuOpen ? 'docs-sidebar-open' : ''}`}
      aria-hidden={isSidebarCollapsed && !isMobileMenuOpen ? true : undefined}
      data-testid="docs-sidebar"
    >
      <button
        type="button"
        className="docs-sidebar-close"
        aria-label="Close article menu"
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>

      {showHeader && (
        <div className="docs-sidebar-shell-header">
          <div className="docs-sidebar-shell-row">
            <Link to="/docs" className="docs-sidebar-brand" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="docs-sidebar-brand-mark">
                <img src="/favicon.png" alt="" width={24} height={24} />
              </span>
              <span className="docs-sidebar-brand-copy">
                <strong>VoiceInk</strong>
                <span>Docs</span>
              </span>
            </Link>

            <button
              type="button"
              className="docs-sidebar-collapse"
              aria-label="Hide docs sidebar"
              onClick={onToggleSidebar}
            >
              <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <DocsSearchBar />
        </div>
      )}

      <nav className="docs-sidebar-nav" aria-label="Article navigation">
        {Object.entries(groupedDocs).map(([category, docs]) => {
          const isCurrentCategory = currentCategory === category;

          return (
            <section
              key={category}
              className={`docs-sidebar-section ${isCurrentCategory ? 'docs-sidebar-section-current' : ''}`}
            >
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="docs-sidebar-section-button"
                aria-expanded={openCategories[category]}
              >
                <span className="docs-sidebar-section-copy">
                  <span className="docs-sidebar-section-title">
                    {category}
                  </span>
                </span>
                <ChevronDown
                  className={`docs-sidebar-chevron ${openCategories[category] ? 'docs-sidebar-chevron-open' : ''}`}
                  aria-hidden="true"
                />
              </button>
              {openCategories[category] && (
                <div className="docs-sidebar-links">
                  {docs.map((doc) => (
                    <NavLink
                      key={doc.slug}
                      to={`/docs/${doc.slug}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `docs-sidebar-link ${isActive ? 'docs-sidebar-link-active' : ''}`
                      }
                    >
                      <span className="docs-sidebar-link-label">{doc.title}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </nav>

      <div className="docs-sidebar-footer">
        <Link
          to="/"
          onClick={() => setIsMobileMenuOpen(false)}
          className="docs-sidebar-home-link"
        >
          <Home className="h-4 w-4" aria-hidden="true" />
          <span>Home</span>
        </Link>
      </div>
    </aside>
  );
};
